import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'

import { breakpoints, zIndex } from '@/styles'
import { useFilters } from '@/hooks/use-filters'
import { AlertButtonList } from '@/features/Listings/buttons'
import { useFiltersModal } from '@/features/Listings/hooks/use-filters-modal'
import { Button } from '@/components/button-new'
import { FiltersIcon } from '@/components/icons'

import ActiveFilterIndicators from './active-filter-indicators'
import BedBathFilter from './bed-bath-filter'
import FilterItem from './filter-item'
import FurnishingFilter from './furnishing-filter'
import PriceFilter from './price-filter'
import { disableBedrooms, getFilterValues } from './utils'

const Wrapper = styled.div`
  z-index: ${zIndex.searchFilters};
  position: relative;
  width: 100%;
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    z-index: unset;
  }
`

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
`

const hideListViewFilters = css`
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    display: none;
  }
`

const SecondaryButtons = styled.div`
  display: flex;
  margin-left: auto;
  gap: 12px;
`

const FiltersButton = styled(Button)`
  font-size: 14px;
  border-radius: 4px;
  padding: 12px 20px;
  ${hideListViewFilters}
`

const ListingFilter = ({
  isMapMode,
  hideAlertButton,
  hideResultsCount,
  boundaries,
  forceClose,
  hideActiveFilters,
  isExt,
  isUniversityPage,
  centerPoint,
  companyId
}) => {
  const { t } = useTranslation()
  const [filters, setFilter] = useFilters()
  const filterValues = getFilterValues(filters)
  const [showFiltersModal] = useFiltersModal({
    isMapMode,
    boundaries,
    hideResultsCount,
    isUniversityPage,
    centerPoint,
    companyId
  })

  const clickId = 'map-filters-open-click'

  const handleClick = () => {
    window.dataLayer.push({
      event: clickId
    })
    showFiltersModal()
  }

  const setFilterMerging = newFilter => {
    const filterParams = { ...filterValues, ...newFilter, page: null }
    setFilter(filterParams)
  }

  const priceFilters = isUniversityPage
    ? { ...filters, search_only_verified: true }
    : filters

  const priceFilter = () => (
    <FilterItem
      forceClose={forceClose}
      title={t('filters:b.listing_filter.edit.price.label', 'Price')}
      customStyle={hideListViewFilters}
    >
      <PriceFilter
        companyId={companyId}
        savedFilters={priceFilters}
        value={{
          min_price: filters.min_price,
          max_price: filters.max_price
        }}
        onChange={({ min_price, max_price }) =>
          setFilterMerging({ min_price, max_price })
        }
        boundaries={boundaries}
        isExt={isExt}
      />
    </FilterItem>
  )

  const bedBathFilter = () => (
    <FilterItem
      forceClose={forceClose}
      title={t('filters:b.listing_filter.edit.bed_bath.label', 'Bed & Bath')}
      customStyle={hideListViewFilters}
    >
      <BedBathFilter
        value={{
          bedroom_count: filters.bedroom_count,
          bathroom_count: filters.bathroom_count
        }}
        disableBed={disableBedrooms(filters.housing_types, filters.unit_types)}
        onChange={({ bedroom_count, bathroom_count }) =>
          setFilterMerging({ bedroom_count, bathroom_count })
        }
        isExt={isExt}
      />
    </FilterItem>
  )

  const furnishingFilter = () => (
    <FilterItem
      forceClose={forceClose}
      title={t('filters:b.listing_filter.edit.furnishing.label', 'Furnishing')}
      customStyle={hideListViewFilters}
    >
      <FurnishingFilter
        features={filters.features}
        onChange={({ features }) => setFilterMerging({ features })}
        isExt={isExt}
      />
    </FilterItem>
  )

  return (
    <Wrapper>
      <Content>
        <FiltersButton
          theme="blueSecondary"
          size="small"
          id={clickId}
          onClick={handleClick}
        >
          <FiltersIcon width={18} height={14} right={4} />
          {t('filters:b.listing_filter.edit.filters.label', 'Filters')}
        </FiltersButton>
        {priceFilter()}
        {bedBathFilter()}
        {furnishingFilter()}
        <SecondaryButtons>
          {!hideAlertButton && <AlertButtonList />}
        </SecondaryButtons>
      </Content>
      {!hideActiveFilters && (
        <ActiveFilterIndicators
          filters={filters}
          onChange={setFilterMerging}
          isMapMode={isMapMode}
          isUniversityPage={isUniversityPage}
        />
      )}
    </Wrapper>
  )
}

export default ListingFilter
